import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Language {
  code: string;
  name: string;
  flag: string;
}

interface NavItem {
  name: string;
  path: string;
}

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation('common');

  useEffect(() => {
    setMounted(true);
  }, []);

  // Fallback nav items in case translations aren't loaded
  const navItems: NavItem[] = [
    { name: mounted ? t('nav.about', 'ABOUT') : 'ABOUT', path: '/about' },
    { name: mounted ? t('nav.services', 'SERVICES') : 'SERVICES', path: '/services' },
    { name: mounted ? t('nav.contact', 'CONTACT') : 'CONTACT', path: '/contacts' }
  ];

  const languages: Language[] = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'sk', name: 'Slovenčina', flag: '🇸🇰' }
  ];

  const currentLanguage: Language = languages.find(lang => lang.code === (i18n?.language || 'en')) || languages[0];

  const changeLanguage = (langCode: string) => {
    try {
      if (i18n?.changeLanguage) {
        i18n.changeLanguage(langCode);
      }
      setIsLangOpen(false);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  const handleMobileMenuToggle = () => {
    try {
      setIsMenuOpen(prev => !prev);
    } catch (error) {
      console.error('Failed to toggle mobile menu:', error);
    }
  };

  const handleLangToggle = () => {
    try {
      setIsLangOpen(prev => !prev);
    } catch (error) {
      console.error('Failed to toggle language selector:', error);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-transparent">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center cursor-pointer">
            <img 
              src="https://static.readdy.ai/image/14febd11ed764a2e4131edc63c3ba26f/8ff2eb1ab6c7e21da6a0a6b3609c06c8.png" 
              alt="BulkCom Logo" 
              className="h-12 w-auto object-contain drop-shadow-[0_0_8px_rgba(0,0,0,0.8)]"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-10">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-lg font-medium tracking-wider transition-colors duration-200 cursor-pointer whitespace-nowrap uppercase ${
                  location.pathname === item.path
                    ? 'text-white'
                    : 'text-[#2E8BC0] hover:text-white'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={handleLangToggle}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <span className="text-xl">{currentLanguage.flag}</span>
                <span className="text-lg font-medium text-white">{currentLanguage.code.toUpperCase()}</span>
                <i className={`ri-arrow-down-s-line text-white transition-transform duration-200 ${isLangOpen ? 'rotate-180' : ''}`}></i>
              </button>
              
              {isLangOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full flex items-center space-x-3 px-4 py-2 hover:bg-gray-100 transition-colors duration-200 cursor-pointer ${
                        i18n?.language === lang.code ? 'bg-gray-50' : ''
                      }`}
                    >
                      <span className="text-xl">{lang.flag}</span>
                      <span className="text-sm font-normal text-[#0D1B2A]">{lang.name}</span>
                      {i18n?.language === lang.code && (
                        <i className="ri-check-line text-[#2E8BC0] ml-auto"></i>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={handleMobileMenuToggle}
            className="md:hidden w-10 h-10 flex items-center justify-center text-white hover:text-[#2E8BC0] transition-colors duration-200 cursor-pointer"
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden py-3 bg-[#0D1B2A] backdrop-blur-sm rounded-lg mt-2 shadow-lg">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block py-2 px-4 text-base font-medium tracking-wide transition-colors duration-200 cursor-pointer uppercase ${
                  location.pathname === item.path
                    ? 'text-[#2E8BC0]'
                    : 'text-white hover:text-[#2E8BC0]'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Mobile Language Selector */}
            <div className="mt-3 pt-3 border-t border-white/20 px-4">
              <div className="text-xs font-semibold text-white/60 mb-2 uppercase tracking-wide">Language</div>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    changeLanguage(lang.code);
                    setIsMenuOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 py-2 transition-colors duration-200 cursor-pointer ${
                    i18n?.language === lang.code ? 'text-[#2E8BC0]' : 'text-white hover:text-[#2E8BC0]'
                  }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span className="text-sm font-normal">{lang.name}</span>
                  {i18n?.language === lang.code && (
                    <i className="ri-check-line ml-auto"></i>
                  )}
                </button>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
